"use client";

import "./landing.css";
import Image from "next/image";
import Link from "next/link";
import { MotionConfig } from "framer-motion";
import { useEffect, useSyncExternalStore } from "react";
import Footer from "@/components/Footer";
import type { Lang } from "@/lib/lang";
import { archivo, geist } from "./fonts";
import { Nav } from "./Top";
import { Overline, Reveal } from "./ui";
import { useLandingLang } from "./useLang";

/*
 * Players live in the app. Signing up or in as a player on the web lands here:
 * the store for the phone in your hand, or a QR code when you're on a computer.
 * The list below is only what the app does today — nothing promised.
 */

export const APP_STORE_URL = "https://apps.apple.com/us/app/draffte-passport/id6760527700";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=draffte.passport.app";

type Device = "ios" | "android" | "desktop";

function detect(): Device {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  // iPadOS asks for the desktop site and says "Macintosh" — a touch screen gives it away.
  if (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  return "desktop";
}
const noop = () => () => {};
function useDevice(): Device {
  return useSyncExternalStore(noop, detect, () => "desktop");
}

const K = {
  over: { en: "Draffte Passport · the app", es: "Draffte Passport · la app" },
  title: { en: ["Your Passport", "lives in the app."], es: ["Tu Passport", "vive en la app."] },
  sub: {
    en: "Every game you play, your clips and your rating are in the Draffte Passport app — and it does more than the website ever will. Download it, sign up with your email, and join your team with your coach's code.",
    es: "Cada partido que juegas, tus clips y tu calificación están en la app Draffte Passport — y hace más de lo que la web hará nunca. Descárgala, regístrate con tu correo y únete a tu equipo con el código de tu entrenador.",
  },
  signedIn: {
    en: "Already have an account? Sign in on the app with the same email and password.",
    es: "¿Ya tienes cuenta? Inicia sesión en la app con el mismo correo y contraseña.",
  },
  scan: { en: "Scan with your phone's camera", es: "Escanea con la cámara de tu celular" },
  scanSub: {
    en: "It opens the App Store on an iPhone and Google Play on Android.",
    es: "Abre el App Store en iPhone y Google Play en Android.",
  },
  appStoreTop: { en: "Download on the", es: "Descárgala en el" },
  playTop: { en: "Get it on", es: "Disponible en" },
  alsoOn: { en: "Also on", es: "También en" },
  inside: { en: "What's in the app", es: "Qué hay en la app" },
  insideTitle: { en: "Everything your games say about you.", es: "Todo lo que tus partidos dicen de ti." },
  features: [
    {
      t: { en: "Your Passport", es: "Tu Passport" },
      d: {
        en: "Your player card: position, team and your OVR — the rating built from the games you've played.",
        es: "Tu tarjeta de jugador: posición, equipo y tu media — la calificación que sale de los partidos que has jugado.",
      },
    },
    {
      t: { en: "My Games", es: "Mis partidos" },
      d: {
        en: "Every game you played, with your minutes, passes, shots, duels and your rating for that game.",
        es: "Cada partido que jugaste, con tus minutos, pases, tiros, duelos y tu calificación de ese partido.",
      },
    },
    {
      t: { en: "Your moments", es: "Tus jugadas" },
      d: {
        en: "Clips of your plays from the match video — what went well and what to work on — full screen.",
        es: "Clips de tus jugadas sacados del video del partido — lo que salió bien y lo que hay que mejorar — en pantalla completa.",
      },
    },
    {
      t: { en: "The match video", es: "El video del partido" },
      d: {
        en: "When your team's camera filmed the game, watch it whole, with the picture following the ball.",
        es: "Cuando la cámara de tu equipo grabó el partido, míralo completo, con la imagen siguiendo el balón.",
      },
    },
    {
      t: { en: "Career & scout report", es: "Carrera e informe de scout" },
      d: {
        en: "Your season in numbers, how your rating moves game to game, and how a scout would read your profile.",
        es: "Tu temporada en números, cómo cambia tu calificación partido a partido y cómo leería un scout tu perfil.",
      },
    },
    {
      t: { en: "Rankings", es: "Rankings" },
      d: {
        en: "See where you ranked among the players in each game.",
        es: "Mira en qué puesto quedaste entre los jugadores de cada partido.",
      },
    },
  ],
  ovrOver: { en: "About your OVR", es: "Sobre tu media" },
  ovr: {
    en: "New players unlock their OVR after 3 rated games — the app shows how many you have so far. Already have an OVR? It stays exactly as it is.",
    es: "Los jugadores nuevos desbloquean su media con 3 partidos calificados — la app te muestra cuántos llevas. ¿Ya tienes media? Se queda tal como está.",
  },
  coach: { en: "Are you a coach?", es: "¿Eres entrenador?" },
  coachLink: { en: "Coach sign-up is on the web", es: "El registro de entrenadores es en la web" },
} as const;

function AppleGlyph() {
  return (
    <svg viewBox="0 0 384 512" width="22" height="26" aria-hidden="true" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 24 26" width="22" height="24" aria-hidden="true">
      <path d="M1.2.6 13.4 12.9 1.2 25.2a1.6 1.6 0 0 1-.6-1.3V1.9c0-.5.2-1 .6-1.3Z" fill="#34A853" />
      <path d="m17.5 8.8-4.1 4.1 4.1 4.1 4.6-2.6c1.3-.8 1.3-2.2 0-3l-4.6-2.6Z" fill="#FBBC04" />
      <path d="M1.2.6c.4-.4 1.1-.5 1.8-.1l14.5 8.3-4.1 4.1L1.2.6Z" fill="#EA4335" />
      <path d="M13.4 12.9l4.1 4.1L3 25.3c-.7.4-1.4.3-1.8-.1l12.2-12.3Z" fill="#4285F4" />
    </svg>
  );
}

function StoreButton({ store, lang, big }: { store: "ios" | "android"; lang: Lang; big?: boolean }) {
  const ios = store === "ios";
  return (
    <a
      href={ios ? APP_STORE_URL : PLAY_STORE_URL}
      className={`group inline-flex items-center gap-3 rounded-[16px] transition-[transform,background,box-shadow] duration-300 hover:-translate-y-px ${big ? "px-6 py-4" : "px-5 py-3"}`}
      style={
        big
          ? { background: "#fff", color: "var(--sn-bg)", boxShadow: "0 18px 50px -18px rgba(255,255,255,.45)" }
          : { background: "var(--sn-glass)", color: "#fff", border: "1px solid var(--sn-hair)" }
      }
    >
      {ios ? <AppleGlyph /> : <PlayGlyph />}
      <span className="flex flex-col text-left leading-none">
        <span className="text-[11px] font-medium" style={{ opacity: 0.7 }}>{ios ? K.appStoreTop[lang] : K.playTop[lang]}</span>
        <span className={`mt-1 font-semibold tracking-[-0.01em] ${big ? "text-[21px]" : "text-[17px]"}`}>{ios ? "App Store" : "Google Play"}</span>
      </span>
    </a>
  );
}

function Stores({ device, lang }: { device: Device; lang: Lang }) {
  if (device === "desktop") {
    return (
      <div className="flex flex-col gap-8 sm:flex-row sm:items-center">
        <div className="shrink-0 rounded-[24px] bg-white p-4 shadow-[0_30px_80px_-30px_rgba(226,189,114,.55)]">
          <Image src="/landing/get-app-qr.svg" alt="" width={156} height={156} unoptimized priority />
        </div>
        <div>
          <p className="sn-wide text-[19px]">{K.scan[lang]}</p>
          <p className="mt-2 max-w-[22rem] text-[14.5px] leading-relaxed" style={{ color: "var(--sn-fg2)" }}>{K.scanSub[lang]}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <StoreButton store="ios" lang={lang} />
            <StoreButton store="android" lang={lang} />
          </div>
        </div>
      </div>
    );
  }
  const other = device === "ios" ? "android" : "ios";
  return (
    <div className="flex flex-col items-start gap-5">
      <StoreButton store={device} lang={lang} big />
      <p className="flex flex-wrap items-center gap-3 text-[13px]" style={{ color: "var(--sn-fg3)" }}>
        {K.alsoOn[lang]} <StoreButton store={other} lang={lang} />
      </p>
    </div>
  );
}

export default function GetAppPage() {
  const [lang, onLang] = useLandingLang();
  const device = useDevice();

  // The QR code carries ?go=1: a phone that scanned it goes straight to its store.
  useEffect(() => {
    if (device === "desktop") return;
    if (new URLSearchParams(window.location.search).get("go") !== "1") return;
    window.location.replace(device === "ios" ? APP_STORE_URL : PLAY_STORE_URL);
  }, [device]);

  return (
    <MotionConfig reducedMotion="user">
      <div className={`sn ${archivo.variable} ${geist.variable} min-h-screen`}>
        <Nav lang={lang} onLang={onLang} page="app" />
        <main>
          <section className="sn-grain relative isolate overflow-hidden pb-16 pt-28 sm:pb-24 sm:pt-36">
            <div className="pointer-events-none absolute inset-0 -z-10" style={{ background: "radial-gradient(50% 60% at 80% 10%, rgba(226,189,114,.16), rgba(226,189,114,0) 70%), radial-gradient(40% 50% at 8% 20%, rgba(40,60,110,.28), rgba(40,60,110,0) 70%)" }} />
            <div className="mx-auto grid max-w-[1360px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
              <div>
                <p className="sn-over sn-up" style={{ color: "var(--sn-gold)" }}>{K.over[lang]}</p>
                <h1 className="sn-wide sn-up mt-5 text-[clamp(34px,5vw,72px)]" style={{ animationDelay: ".06s" }}>
                  {K.title[lang].map((l, i) => (
                    <span key={l} className="block" style={i ? { color: "rgba(255,255,255,.55)" } : undefined}>{l}</span>
                  ))}
                </h1>
                <p className="sn-body sn-up mt-6" style={{ animationDelay: ".12s" }}>{K.sub[lang]}</p>
                <div className="sn-up mt-10" style={{ animationDelay: ".18s" }}>
                  <Stores device={device} lang={lang} />
                </div>
                <p className="mt-8 max-w-[34rem] text-[13.5px] leading-relaxed" style={{ color: "var(--sn-fg3)" }}>{K.signedIn[lang]}</p>
              </div>

              <div className="relative mx-auto aspect-[4/5] w-full max-w-[440px] overflow-hidden rounded-[32px]" style={{ boxShadow: "0 0 0 1px var(--sn-hair), 0 50px 140px -40px rgba(0,0,0,.95)" }}>
                <Image src="/landing/player.webp" alt="" fill priority sizes="(min-width:1024px) 440px, 90vw" className="object-cover" style={{ objectPosition: "50% 18%" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,13,0) 45%, rgba(5,7,13,.92) 100%)" }} />
                <div className="sn-glass absolute inset-x-5 bottom-5 rounded-[20px] p-5">
                  <Overline gold>{K.ovrOver[lang]}</Overline>
                  <div className="mt-3 flex gap-1.5" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="h-[5px] w-10 rounded-full" style={{ background: i < 1 ? "var(--sn-gold)" : "var(--sn-hair)" }} />
                    ))}
                  </div>
                  <p className="mt-3 text-[14px] leading-snug" style={{ color: "rgba(255,255,255,.86)" }}>{K.ovr[lang]}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 sm:py-24" aria-labelledby="sn-app-inside">
            <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
              <Reveal><Overline gold>{K.inside[lang]}</Overline></Reveal>
              <Reveal delay={0.06}><h2 id="sn-app-inside" className="sn-wide mt-4 max-w-[18ch] text-[clamp(28px,3.6vw,52px)]">{K.insideTitle[lang]}</h2></Reveal>
              <div className="mt-12 grid gap-x-10 border-t sm:grid-cols-2 lg:grid-cols-3" style={{ borderColor: "var(--sn-hair)" }}>
                {K.features.map((f, i) => (
                  <Reveal key={f.t.en} delay={0.04 * i}>
                    <div className="border-b py-7" style={{ borderColor: "var(--sn-hair)" }}>
                      <h3 className="sn-wide text-[18px]">{f.t[lang]}</h3>
                      <p className="mt-2.5 text-[15px] leading-relaxed" style={{ color: "var(--sn-fg2)" }}>{f.d[lang]}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <p className="mt-12 text-[14px]" style={{ color: "var(--sn-fg3)" }}>
                {K.coach[lang]}{" "}
                <Link href="/signup/coach" className="underline decoration-[var(--sn-gold)] underline-offset-4" style={{ color: "#fff" }}>{K.coachLink[lang]}</Link>
              </p>
            </div>
          </section>
        </main>
        <Footer lang={lang} />
      </div>
    </MotionConfig>
  );
}
